import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router";
import {
  calcularTroco,
  FORMA_PAGAMENTO_ROTULO,
  FORMAS_PAGAMENTO,
  formatarHora,
  formatarReais,
  totalDosItens,
} from "@caixaflow/shared";
import { api, qs } from "../../api/cliente";
import type { CaixaDetalhe, Cardapio, ItemCardapio, UsuarioSessao, Venda } from "../../api/tipos";
import { EntradaReais } from "../../components/Campos";
import { Carregando, FalhaAoCarregar } from "../../components/Estados";
import { Modal } from "../../components/Modal";
import { ModalVenda } from "../../components/ModalVenda";
import { FormOferta } from "../../components/Ofertas";
import { useToast } from "../../components/Toast";
import { novoId } from "../../lib/id";
import { registrarVendaNoAparelho } from "../../offline/envio";
import type { VendaNaFila } from "../../offline/fila";
import { useFila } from "../../offline/useFila";
import { comUltimoConhecido } from "../../offline/ultimoConhecido";
import { comandaVazia, disponivel, itensDaComanda, reduzirComanda } from "../../pdv/comanda";
import { PainelFila } from "./PainelFila";

export function Pdv({ caixa, usuario }: { caixa: CaixaDetalhe; usuario: UsuarioSessao }) {
  const eventoId = caixa.evento.id;
  // Sem internet, o PDV segue com o último cardápio e as últimas vendas conhecidos.
  const cardapio = useQuery({
    queryKey: ["cardapio", eventoId],
    queryFn: () => comUltimoConhecido(`cardapio:${eventoId}`, () => api<Cardapio>(`/eventos/${eventoId}/cardapio`)),
    refetchInterval: 60_000,
  });
  const vendas = useQuery({
    queryKey: ["vendas", caixa.id],
    queryFn: () =>
      comUltimoConhecido(`vendas:${caixa.id}`, () => api<Venda[]>(`/vendas${qs({ caixaId: caixa.id, limite: 15 })}`)),
  });
  const fila = useFila(usuario.id);
  const [comanda, despachar] = useReducer(reduzirComanda, comandaVazia);
  const [salvando, setSalvando] = useState(false);
  const [aberta, setAberta] = useState<Venda | null>(null);
  const [ofertando, setOfertando] = useState(false);
  const [verFila, setVerFila] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();

  // Enquanto o PDV está na tela: o aviso (toast) sobe acima da comanda fixa e a página reserva
  // espaço no fim para nada (últimas vendas, rodapé) ficar escondido atrás dela.
  useEffect(() => {
    const raiz = document.documentElement.style;
    raiz.setProperty("--toast-base", "270px");
    raiz.setProperty("--espaco-comanda", "250px");
    return () => {
      raiz.removeProperty("--toast-base");
      raiz.removeProperty("--espaco-comanda");
    };
  }, []);

  const itens = useMemo(() => cardapio.data?.itens ?? [], [cardapio.data]);
  const permiteNegativo = cardapio.data?.evento.permiteEstoqueNegativo ?? false;
  const filaDoCaixa = fila.itens.filter((v) => v.caixaId === caixa.id);

  // Vendas guardadas no aparelho já saíram do balcão, mas o servidor ainda não sabe.
  const vendidoNaFila = useMemo(() => {
    const m = new Map<string, number>();
    for (const v of fila.itens) {
      if (v.eventoId !== eventoId) continue;
      for (const i of v.resumo.itens) m.set(i.produtoId, (m.get(i.produtoId) ?? 0) + i.quantidade);
    }
    return m;
  }, [fila.itens, eventoId]);

  const itensComanda = itensDaComanda(comanda, itens);
  const total = totalDosItens(itensComanda);
  const recebido = comanda.forma === "DINHEIRO" ? comanda.recebidoCentavos : null;
  const troco = recebido !== null ? calcularTroco(total, recebido) : null;
  const faltaDinheiro = recebido !== null && troco === null;

  const restante = (item: ItemCardapio, contarComanda = true) =>
    disponivel(item, vendidoNaFila.get(item.produtoId) ?? 0, contarComanda ? (comanda.quantidades[item.produtoId] ?? 0) : 0, permiteNegativo);

  const somar = (item: ItemCardapio) => {
    const r = restante(item);
    if (r !== null && r <= 0) {
      toast(`Sem estoque de ${item.nome}`, "erro");
      return;
    }
    despachar({ tipo: "somar", produtoId: item.produtoId });
  };

  const registrar = async () => {
    if (!itensComanda.length || !comanda.forma || salvando || faltaDinheiro) return;
    setSalvando(true);
    const anterior = comanda;
    const id = novoId();
    const agora = new Date().toISOString();
    despachar({ tipo: "limpar" });
    try {
      const r = await registrarVendaNoAparelho({
        id,
        usuarioId: usuario.id,
        caixaId: caixa.id,
        eventoId,
        criadaLocalEm: agora,
        payload: {
          id,
          caixaId: caixa.id,
          formaPagamento: anterior.forma!,
          itens: itensComanda.map(({ produtoId, quantidade, precoUnitCentavos }) => ({ produtoId, quantidade, precoUnitCentavos })),
          recebidoCentavos: recebido,
          criadaEm: agora,
          viaFilaOffline: false,
        },
        resumo: { itens: itensComanda, totalCentavos: total, formaPagamento: anterior.forma! },
      });
      if (r.situacao === "ENVIADA") {
        toast(`Venda de ${formatarReais(r.venda.totalCentavos)} registrada`);
      } else if (r.situacao === "GUARDADA") {
        toast("Sem conexão: venda guardada no aparelho. Ela será enviada sozinha.", "info");
      } else {
        despachar({ tipo: "restaurar", comanda: anterior });
        toast(r.erro.mensagem, "erro");
      }
      qc.invalidateQueries({ queryKey: ["vendas", caixa.id] });
      qc.invalidateQueries({ queryKey: ["cardapio", eventoId] });
    } finally {
      setSalvando(false);
    }
  };

  if (cardapio.isPending) return <Carregando texto="Carregando cardápio…" />;
  if (cardapio.error && !cardapio.data) {
    return <FalhaAoCarregar erro={cardapio.error} tentarDeNovo={() => cardapio.refetch()} />;
  }

  const categorias: [string, ItemCardapio[]][] = [];
  for (const item of itens) {
    const ultima = categorias.at(-1);
    if (ultima && ultima[0] === item.categoria.nome) ultima[1].push(item);
    else categorias.push([item.categoria.nome, [item]]);
  }

  const rotuloRegistrar = salvando
    ? "Registrando…"
    : !itensComanda.length
      ? "Registrar venda"
      : !comanda.forma
        ? "Escolha o pagamento"
        : faltaDinheiro
          ? "Recebido menor que o total"
          : `Registrar ${formatarReais(total)}`;

  return (
    <div className="pdv">
      <div className="pdv-topo">
        <span className="info">
          <b>{caixa.evento.nome}</b> · Caixa #{caixa.numero}
        </span>
        <div className="linha-esq">
          <IndicadorConexao
            online={fila.online}
            pendentes={fila.pendentes.length}
            recusadas={fila.recusadas.length}
            onAbrir={() => setVerFila(true)}
          />
          <Link className="btn pequeno fantasma" to="/caixa/fechar">
            Fechar caixa
          </Link>
        </div>
      </div>

      {fila.recusadas.length > 0 && (
        <div className="aviso erro linha" role="alert">
          <span>
            {fila.recusadas.length === 1 ? "1 venda foi recusada" : `${fila.recusadas.length} vendas foram recusadas`} pelo
            servidor e precisa{fila.recusadas.length === 1 ? "" : "m"} de atenção.
          </span>
          <button className="btn pequeno fantasma" onClick={() => setVerFila(true)}>
            Resolver
          </button>
        </div>
      )}

      {itens.length === 0 && (
        <div className="card vazio">O cardápio deste evento está vazio. Peça a um ADMIN para incluir os produtos.</div>
      )}

      {categorias.map(([categoria, lista]) => (
        <section key={categoria}>
          <h2>{categoria}</h2>
          <div className="grid">
            {lista.map((item) => {
              const q = comanda.quantidades[item.produtoId] ?? 0;
              const semComanda = restante(item, false);
              const comComanda = restante(item);
              const esgotado = semComanda !== null && semComanda <= 0;
              const baixo = semComanda !== null && !esgotado && semComanda <= Math.max(item.limiteEstoqueBaixo, 0);
              return (
                <div key={item.produtoId} style={{ position: "relative" }}>
                  <button
                    type="button"
                    className={`prod${q ? " on" : ""}${esgotado ? " esgotado" : ""}`}
                    style={{ width: "100%", height: "100%" }}
                    aria-label={`Adicionar ${item.nome}, ${formatarReais(item.precoCentavos)}${q ? `, ${q} na comanda` : ""}${esgotado ? ", esgotado" : ""}`}
                    aria-disabled={esgotado}
                    onClick={() => somar(item)}
                  >
                    <span className="n">{item.nome}</span>
                    {esgotado ? (
                      <span className="estoque">Esgotado</span>
                    ) : (
                      baixo && <span className="estoque">Restam {comComanda}</span>
                    )}
                    <span className="p">{formatarReais(item.precoCentavos)}</span>
                    {q > 0 && (
                      <span className="qty" aria-hidden="true">
                        {q}
                      </span>
                    )}
                  </button>
                  {q > 0 && (
                    <button
                      type="button"
                      className="minus"
                      aria-label={`Remover um ${item.nome}`}
                      onClick={() => despachar({ tipo: "diminuir", produtoId: item.produtoId })}
                    >
                      −
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <section>
        <h2>Últimas vendas</h2>
        <UltimasVendas
          fila={filaDoCaixa}
          vendas={vendas.data ?? []}
          carregando={vendas.isPending}
          onAbrir={setAberta}
          onVerFila={() => setVerFila(true)}
        />
        <Link className="link-botao" style={{ display: "inline-block", marginTop: 8, paddingLeft: 0 }} to={`/vendas?caixa=${caixa.id}&status=TODAS`}>
          Ver todas as vendas deste caixa →
        </Link>
      </section>

      <div className="bar">
        <div className="bar-in">
          <div className="bar-top">
            <div className="resumo-itens">
              {itensComanda.length
                ? itensComanda.map((i) => `${i.quantidade}× ${i.nome}`).join(" · ")
                : "Toque nos itens para montar a venda"}
            </div>
            <div className="total" aria-live="polite">
              {formatarReais(total)}
            </div>
          </div>
          <div className="pays" role="group" aria-label="Forma de pagamento">
            {FORMAS_PAGAMENTO.map((f) => (
              <button key={f} type="button" aria-pressed={comanda.forma === f} onClick={() => despachar({ tipo: "forma", forma: f })}>
                {FORMA_PAGAMENTO_ROTULO[f]}
              </button>
            ))}
          </div>
          {comanda.forma === "DINHEIRO" && total > 0 && (
            <div className="troco">
              <label htmlFor="recebido">Recebido R$</label>
              <EntradaReais id="recebido" centavos={comanda.recebidoCentavos} onChange={(c) => despachar({ tipo: "recebido", centavos: c })} />
              <span>
                Troco:{" "}
                {recebido === null ? (
                  <b>—</b>
                ) : troco === null ? (
                  <b className="falta">faltam {formatarReais(total - recebido)}</b>
                ) : (
                  <b>{formatarReais(troco)}</b>
                )}
              </span>
            </div>
          )}
          <div className="acoes-comanda">
            <button type="button" className="btn fantasma" onClick={() => despachar({ tipo: "limpar" })}>
              Limpar
            </button>
            <button
              type="button"
              className="btn fantasma"
              disabled={!itensComanda.length || !fila.online}
              title={fila.online ? "Registrar os itens da comanda como oferta (sem pagamento)" : "Oferta precisa de internet"}
              onClick={() => setOfertando(true)}
            >
              Oferta
            </button>
            <button
              type="button"
              className="btn primario"
              disabled={!itensComanda.length || !comanda.forma || salvando || faltaDinheiro}
              onClick={registrar}
            >
              {rotuloRegistrar}
            </button>
          </div>
        </div>
      </div>

      <ModalVenda vendaId={aberta?.id ?? null} inicial={aberta ?? undefined} onFechar={() => setAberta(null)} />
      <Modal aberto={ofertando} titulo="Registrar oferta" onFechar={() => setOfertando(false)}>
        {ofertando && (
          <FormOferta
            eventoId={eventoId}
            caixaId={caixa.id}
            itens={itensComanda.map((i) => ({ produtoId: i.produtoId, nome: i.nome, quantidade: i.quantidade, precoCentavos: i.precoUnitCentavos }))}
            onVoltar={() => setOfertando(false)}
            onPronto={() => {
              despachar({ tipo: "limpar" });
              setOfertando(false);
            }}
          />
        )}
      </Modal>
      <PainelFila aberto={verFila} onFechar={() => setVerFila(false)} fila={fila} />
    </div>
  );
}

function IndicadorConexao(props: { online: boolean; pendentes: number; recusadas: number; onAbrir: () => void }) {
  const { online, pendentes, recusadas, onAbrir } = props;
  return (
    <div className="conexao" role="status">
      <span>
        <span className={`ponto${online ? "" : " off"}`} aria-hidden="true" />
        {online ? "Online" : "Sem conexão"}
      </span>
      {pendentes > 0 && (
        <button className="pendentes-btn" onClick={onAbrir}>
          {pendentes === 1 ? "1 venda pendente de envio" : `${pendentes} vendas pendentes de envio`}
        </button>
      )}
      {recusadas > 0 && (
        <button className="pendentes-btn recusadas" onClick={onAbrir}>
          {recusadas === 1 ? "1 recusada" : `${recusadas} recusadas`}
        </button>
      )}
    </div>
  );
}

function descricao(itens: { nome?: string; nomeProduto?: string; quantidade: number }[]) {
  return itens.map((i) => `${i.quantidade}× ${i.nome ?? i.nomeProduto}`).join(", ");
}

function UltimasVendas(props: {
  fila: VendaNaFila[];
  vendas: Venda[];
  carregando: boolean;
  onAbrir: (v: Venda) => void;
  onVerFila: () => void;
}) {
  const { fila, vendas, carregando, onAbrir, onVerFila } = props;
  if (!fila.length && !vendas.length) {
    return (
      <div className="card vazio">
        {carregando ? "Carregando…" : "Nenhuma venda ainda. Toque nos itens, escolha o pagamento e registre."}
      </div>
    );
  }
  return (
    <div className="card">
      {[...fila].reverse().map((v) => (
        <div key={v.id} className="venda-linha">
          <span className="t">{formatarHora(v.criadaLocalEm)}</span>
          <span className="d">
            {descricao(v.resumo.itens)}
            <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.resumo.formaPagamento]}</span>
            <span className={`chip ${v.estado === "RECUSADA" ? "perigo" : "alerta"}`}>
              {v.estado === "RECUSADA" ? "recusada" : "aguardando envio"}
            </span>
          </span>
          <span className="v">{formatarReais(v.resumo.totalCentavos)}</span>
          <button className="link-botao" onClick={onVerFila}>
            Ver
          </button>
        </div>
      ))}
      {vendas.map((v) => (
        <Fragment key={v.id}>
          <div className={`venda-linha${v.status === "CANCELADA" ? " cancelada" : ""}`}>
            <span className="t">{formatarHora(v.criadaEm)}</span>
            <span className="d">
              {descricao(v.itens)}
              <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.formaPagamento]}</span>
              {v.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
              {v.ajustes?.length > 0 && <span className="chip alerta">ajustada</span>}
            </span>
            <span className="v">{formatarReais(v.totalCentavos)}</span>
            <button className="link-botao" onClick={() => onAbrir(v)} aria-label={`Ver venda #${v.numero}`}>
              Ver
            </button>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
